import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	CircularProgress,
	Divider,
	IconButton,
	ListItemText,
	Pagination,
	Stack,
	Typography,
} from '@mui/material';
import CustomButton from '@src/components/CustomButton/CustomButton';
import CustomDataGrid2 from '@src/components/CustomDataGrid/CustomDataGrid2';
import LabeledSelectField from '@src/components/CustomSelect/LabeledSelectField';
import LabeledTextField from '@src/components/CustomTextField/LabeledTextField';
import PageHeader from '@src/components/PageHeader/PageHeader';
import { error } from 'console';
import React, { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CustomAlert from '@src/components/CustomAlert/CustomAlert';
import { DeleteIcon } from '@src/components/Icons';
import CustomModal from '@src/components/CustomModal';
import AutoComplete from '@src/components/CustomSelect/AutoComplete';
import { IsMobileWidth } from '@src/components/MediaQueryUtils';
import { ExpandMore } from '@mui/icons-material';

const REWARD_TYPES = {
	ALL_PRODUCTS: 'ALL_PRODUCTS',
	ALL_IN_CATEGORY: 'ALL_IN_CATEGORY',
	ALL_IN_SUB_CATEGORY: 'ALL_IN_SUB_CATEGORY',
	PRODUCT: 'PRODUCT',
};

function ButtonSpinner() {
	return <CircularProgress size={16} style={{ color: 'var(--default-text-color)' }} />;
}

const REWARD_TYPES_OPTIONS = [
	{
		value: 'All Products',
		id: REWARD_TYPES.ALL_PRODUCTS,
	},
	{
		value: 'All In Category',
		id: REWARD_TYPES.ALL_IN_CATEGORY,
	},
	{
		value: 'All In Sub Category',
		id: REWARD_TYPES.ALL_IN_SUB_CATEGORY,
	},
	{
		value: 'Product',
		id: REWARD_TYPES.PRODUCT,
	},
];

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	width: 100%;
	background-color: var(--chat-header-bg);
	transition: width 0.3s ease;

	.addBtn {
		// width: 172px;
		height: 36px;
		border-radius: 4px;
		padding: 7px 12px 7px 12px;
		margin-bottom: 15px;
	}
	.back {
		// width: 172px;
		height: 36px;
		border-radius: 4px;
		padding: 7px 12px 7px 12px;
		margin-bottom: 15px;
		background-color: white;
		border: 1px solid #e9e9e9;
	}
`;

const GridContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
	padding: 20px;
`;

const GridRow = styled.div`
	display: flex;
	gap: 20px;
`;

const GridItem = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	font-size: 16px;
	font-weight: 600;
	color: var(--color-font-grey);
	flex: 1;
`;

const getColumns = (setShowRewardDeleteModal: any) => [
	{
		field: 'rewardType',
		headerName: 'Reward Type',
		width: 250,
	},
	{
		field: 'rewardDetails',
		headerName: 'Reward Details',
		width: 250,
		renderCell: (params: any) => {
			const { rewardType } = params?.row;
			if (REWARD_TYPES.ALL_IN_CATEGORY === rewardType) {
				return (
					<Box>
						<div>{params?.row?.rewardDetails?.name}</div>
					</Box>
				);
			}
			if (REWARD_TYPES.ALL_IN_SUB_CATEGORY === rewardType) {
				return (
					<Box>
						<div>{params?.row?.rewardDetails?.name}</div>
						<div style={{ fontSize: 12 }}>
							Category : {params?.row?.rewardDetails?.category_name}
						</div>
					</Box>
				);
			}
			if (REWARD_TYPES.PRODUCT === rewardType) {
				return (
					<Box>
						<div>{params?.row?.rewardDetails?.name}</div>
					</Box>
				);
			}
			return <Box></Box>;
		},
	},
	{
		field: 'conversionPercentage',
		headerName: 'Conversion %',
		width: 250,
	},
	{
		field: 'action',
		headerName: 'Action',
		width: 250,
		headerAlign: 'center',
		align: 'center',
		renderCell: (params: any) => {
			return (
				<Box width={'100%'} textAlign={'center'}>
					<IconButton onClick={() => setShowRewardDeleteModal(params?.row.rewardId)}>
						<DeleteIcon />
					</IconButton>
				</Box>
			);
		},
	},
];

function ActiveRewardsTable({
	existingRewards,
	setShowRewardDeleteModal,
	totalPages,
	page,
	handlePageChange,
}: any) {
	const columns = getColumns(setShowRewardDeleteModal);
	return (
		<Box>
			<CustomDataGrid2
				rows={existingRewards.map((reward: any) => ({ ...reward, id: reward?.rewardId }))}
				columns={columns}
				onRowsSelectionHandler={() => {}}
				handleRowClick={() => {}}
				checkboxSelection={false}
				style={{
					transition: 'height 0.3s ease',
					maxHeight: 'calc(100vh - 350px)',
					minHeight: 'calc(100vh - 350px)',
				}}
			/>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					width: '100%',
					padding: '10px',
					marginBottom: 5,
				}}
			>
				<Pagination color="primary" count={totalPages} page={page} onChange={handlePageChange} />
			</Box>
		</Box>
	);
}

function AddNewRewardsBtn({ onClick }: any) {
	return (
		<Stack direction="row" justifyContent="flex-end" mt={2}>
			<CustomButton
				title={'Add New Reward'}
				handleClick={onClick}
				// disabled={loadingData}
				className="addBtn"
			/>
		</Stack>
	);
}

const RedemptionSettingsSchema = Yup.object().shape({
	maxRewardAmountPerOrder: Yup.number().required('Required').typeError('Only Numbers Allowed'),
	minAmountBeforeRedemption: Yup.number().required('Required').typeError('Only Numbers Allowed'),
	rupesConversionPercentage: Yup.number()
		.min(1, 'Only Numbers between 1-100 are allowed')
		.max(100, 'Only Numbers between 1-100 are allowed')
		.required('Required')
		.typeError('Only Numbers Allowed'),
	expiresIn: Yup.number().required('Required').typeError('Only Numbers Allowed'),
	daysToPayOrderAmount: Yup.number().required('Required').typeError('Only Numbers Allowed'),
	minOrderAmountValue: Yup.number().required('Required').typeError('Only Numbers Allowed'),
	maxWalletUsagePerOrder: Yup.number().required('Required').typeError('Only Numbers Allowed'),
});

const initialValues = {
	maxRewardAmountPerOrder: 0,
	minAmountBeforeRedemption: 0,
	rupesConversionPercentage: '0',
	expiresIn: 0,
	daysToPayOrderAmount: 0,
	minOrderAmountValue: 0,
	maxWalletUsagePerOrder: 0,
};

function RedemptionSettings({ settings, updateSettings }: any) {
	const isMobile = IsMobileWidth();
	const [updating, setUpdating] = useState(false);
	const { values, errors, handleChange, handleSubmit, setValues } = useFormik({
		initialValues: initialValues,
		validationSchema: RedemptionSettingsSchema,
		onSubmit: (values) => {
			console.log(values);

			const payload = {
				maxRewardAmountPerOrder: parseFloat(values.maxRewardAmountPerOrder),
				minAmountBeforeRedemption: parseFloat(values.minAmountBeforeRedemption),
				rupesConversionPercentage: String(values.rupesConversionPercentage),
				expiresIn: parseInt(values.expiresIn),
				daysToPayOrderAmount: parseInt(values.daysToPayOrderAmount),
				additionalDetails: {
					minOrderAmountValue: parseFloat(values.minOrderAmountValue),
					maxWalletUsagePerOrder: parseFloat(values.maxWalletUsagePerOrder),
				},
			};
			setUpdating(true);
			updateSettings(payload);
			setUpdating(false);
		},
	});

	function checkIfDataIsSame(newData: any, oldData: any) {
		return (
			newData.maxRewardAmountPerOrder == oldData.maxRewardAmountPerOrder &&
			newData.minAmountBeforeRedemption == oldData.minAmountBeforeRedemption &&
			newData.rupesConversionPercentage == oldData.rupesConversionPercentage &&
			newData.expiresIn == oldData.expiresIn &&
			newData.daysToPayOrderAmount == oldData.daysToPayOrderAmount &&
			newData.minOrderAmountValue == oldData.additionalDetails?.minOrderAmountValue &&
			newData.maxWalletUsagePerOrder == oldData.additionalDetails?.maxWalletUsagePerOrder
		);
	}

	useEffect(() => {
		if (settings) {
			const values = {
				...initialValues,
				maxRewardAmountPerOrder: settings?.maxRewardAmountPerOrder,
				minAmountBeforeRedemption: settings?.minAmountBeforeRedemption,
				rupesConversionPercentage: settings?.rupesConversionPercentage,
				expiresIn: settings?.expiresIn,
				daysToPayOrderAmount: settings?.daysToPayOrderAmount,
				minOrderAmountValue: settings?.additionalDetails?.minOrderAmountValue,
				maxWalletUsagePerOrder: settings?.additionalDetails?.maxWalletUsagePerOrder,
			};
			setValues(values);
		}
	}, [settings]);

	if (isMobile) {
		return (
			<Accordion>
				<AccordionSummary
					expandIcon={<ExpandMore />}
					aria-controls="panel1-content"
					id="panel1-header"
				>
					<Typography component="span">Redemption Settings</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<Stack gap={1} direction={'column'}>
						<LabeledTextField
							label="Max Reward Amount Per Order"
							name="maxRewardAmountPerOrder"
							value={values.maxRewardAmountPerOrder}
							onChange={handleChange}
							styles={{ width: '100%', height: '36px' }}
							placeholder="Eg 1000"
							errorText={errors?.maxRewardAmountPerOrder}
						/>
						<LabeledTextField
							label="Min Reward Amount Before Redemption"
							name="minAmountBeforeRedemption"
							value={values.minAmountBeforeRedemption}
							onChange={handleChange}
							styles={{ width: '100%', height: '36px' }}
							placeholder="Eg 1000"
							errorText={errors?.minAmountBeforeRedemption}
						/>
						<LabeledTextField
							label="Rupee Conversion %"
							name="rupesConversionPercentage"
							value={values.rupesConversionPercentage}
							onChange={handleChange}
							styles={{ width: '100%', height: '36px' }}
							placeholder="Eg 5"
							errorText={errors?.rupesConversionPercentage}
						/>
						<LabeledTextField
							label="Expires in Days"
							name="expiresIn"
							value={values.expiresIn}
							onChange={handleChange}
							styles={{ width: '100%', height: '36px' }}
							placeholder="Eg 10"
							errorText={errors?.expiresIn}
						/>
						<LabeledTextField
							label="Days to Pay Order Amount"
							name="daysToPayOrderAmount"
							value={values.daysToPayOrderAmount}
							onChange={handleChange}
							styles={{ width: '100%', height: '36px' }}
							placeholder="Eg 20"
							errorText={errors?.daysToPayOrderAmount}
						/>
						<LabeledTextField
							label="Minimum Order Value to use Wallet"
							name="minOrderAmountValue"
							value={values.minOrderAmountValue}
							onChange={handleChange}
							styles={{ width: '100%', height: '36px' }}
							placeholder="Eg 5"
							errorText={errors?.minOrderAmountValue}
						/>
						<LabeledTextField
							label="Wallet Redemption Cap per Order (₹)"
							name="maxWalletUsagePerOrder"
							value={values.maxWalletUsagePerOrder}
							onChange={handleChange}
							styles={{ width: '100%', height: '36px' }}
							placeholder="Eg 5"
							errorText={errors?.maxWalletUsagePerOrder}
						/>
						<CustomButton
							title={updating ? <ButtonSpinner /> : 'Update Redemption Settings'}
							handleClick={() => handleSubmit()}
							disabled={updating || checkIfDataIsSame(values, settings)}
							className="addBtn"
						/>
					</Stack>
				</AccordionDetails>
			</Accordion>
		);
	}

	return (
		<Stack gap={2}>
			<Typography variant="h6">Redemption Settings</Typography>
			<Stack direction="row" gap={2}>
				<Stack flex={1}>
					<LabeledTextField
						label="Max Reward Amount Per Order"
						name="maxRewardAmountPerOrder"
						value={values.maxRewardAmountPerOrder}
						onChange={handleChange}
						styles={{ width: '100%', height: '36px' }}
						placeholder="Eg 1000"
						errorText={errors?.maxRewardAmountPerOrder}
					/>
				</Stack>
				<Stack flex={1}>
					<LabeledTextField
						label="Min Reward Amount Before Redemption"
						name="minAmountBeforeRedemption"
						value={values.minAmountBeforeRedemption}
						onChange={handleChange}
						styles={{ width: '100%', height: '36px' }}
						placeholder="Eg 1000"
						errorText={errors?.minAmountBeforeRedemption}
					/>
				</Stack>
				<Stack flex={1}>
					<LabeledTextField
						label="Rupee Conversion %"
						name="rupesConversionPercentage"
						value={values.rupesConversionPercentage}
						onChange={handleChange}
						styles={{ width: '100%', height: '36px' }}
						placeholder="Eg 5"
						errorText={errors?.rupesConversionPercentage}
					/>
				</Stack>
			</Stack>
			<Stack direction="row" gap={2}>
				<Stack flex={1}>
					<LabeledTextField
						label="Expires in Days"
						name="expiresIn"
						value={values.expiresIn}
						onChange={handleChange}
						styles={{ width: '100%', height: '36px' }}
						placeholder="Eg 10"
						errorText={errors?.expiresIn}
					/>
				</Stack>
				<Stack flex={1}>
					<LabeledTextField
						label="Days to Pay Order Amount"
						name="daysToPayOrderAmount"
						value={values.daysToPayOrderAmount}
						onChange={handleChange}
						styles={{ width: '100%', height: '36px' }}
						placeholder="Eg 20"
						errorText={errors?.daysToPayOrderAmount}
					/>
				</Stack>
				<Stack flex={1}>
					<LabeledTextField
						label="Minimum Order Value to use Wallet"
						name="minOrderAmountValue"
						value={values.minOrderAmountValue}
						onChange={handleChange}
						styles={{ width: '100%', height: '36px' }}
						placeholder="Eg 5"
						errorText={errors?.minOrderAmountValue}
					/>
				</Stack>
			</Stack>
			<Stack direction="row" gap={2}>
				<Stack flex={1}>
					<LabeledTextField
						label="Wallet Redemption Cap per Order (₹)"
						name="maxWalletUsagePerOrder"
						value={values.maxWalletUsagePerOrder}
						onChange={handleChange}
						styles={{ width: '100%', height: '36px' }}
						placeholder="Eg 5"
						errorText={errors?.maxWalletUsagePerOrder}
					/>
				</Stack>
				<Stack flex={1}></Stack>
				<Stack flex={1}></Stack>
			</Stack>
			<Stack direction="row" justifyContent="flex-end">
				<CustomButton
					title={updating ? <ButtonSpinner /> : 'Update Redemption Settings'}
					handleClick={() => handleSubmit()}
					disabled={updating || checkIfDataIsSame(values, settings)}
					className="addBtn"
				/>
			</Stack>
		</Stack>
	);
}

function DeleteRewardModalContent({ onConfirm, onCancel }: any) {
	return (
		<Box p={3}>
			<Typography variant="h6">Delete Reward</Typography>
			<Stack gap={2} mt={2} direction={'row'}>
				<CustomButton title={'Cancel'} handleClick={onCancel} />
				<CustomButton title={'Delete'} handleClick={onConfirm} />
			</Stack>
		</Box>
	);
}

function RewardsSettingsHome({ apiService, setAlert, id, navigate }: any) {
	const [showRewardDeleteModal, setShowRewardDeleteModal] = useState(null);
	const [totalPages, setTotalPages] = useState(1);
	const [allRewards, setAllRewards] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const limit = 25;
	const [page, setPage] = useState(1);
	const params = {
		limit: limit,
		page: page,
	};

	function fetchAllRewards() {
		setIsLoading(true);
		apiService.getAllRewards(
			(response: any) => {
				console.log(response);
				const rewardsData = response?.data?.data;
				const { totalCount } = rewardsData || {};
				setAllRewards(rewardsData);
				setTotalPages(Math.ceil(totalCount / limit));
				setIsLoading(false);
			},
			(error: any) => {
				console.log(error);
				setAlert({ message: error.response.data.message, severity: 'error' });
				setIsLoading(false);
			},
			id,
			params
		);
	}

	function updateSettings(updatedData: any) {
		apiService.updateRedemptionSettings(
			updatedData,
			(response: any) => {
				setAlert({ message: 'Redemption Settings Updated Successfully', severity: 'success' });
				fetchAllRewards();
			},
			(error: any) => {
				console.log(error);
				setAlert({ message: error.response.data.message, severity: 'error' });
			},
			id
		);
	}

	useEffect(() => {
		fetchAllRewards();
	}, [page]);

	function handleDeleteReward(rewardId: any) {
		apiService.deleteReward(
			rewardId,
			(response: any) => {
				setAlert({ message: 'Reward Deleted Successfully', severity: 'success' });
				setShowRewardDeleteModal(null);
				fetchAllRewards();
			},
			(error: any) => {
				console.log(error);
				setAlert({ message: error.response.data.message, severity: 'error' });
			},
			id
		);
	}

	const handlePageChange = (event: any, value: number) => {
		if (page !== value) {
			setPage(value);
		}
	};

	if (isLoading) {
		return (
			<Box
				width={'100%'}
				textAlign={'center'}
				height={'100%'}
				display={'flex'}
				justifyContent={'center'}
				alignItems={'center'}
			>
				<ButtonSpinner />
			</Box>
		);
	}

	return (
		<Box sx={{ p: 2, overflowY: 'auto' }}>
			<RedemptionSettings
				settings={allRewards?.redemptionSettings || {}}
				updateSettings={updateSettings}
			/>
			<Divider />

			<AddNewRewardsBtn onClick={() => navigate(`/suppliers/${id}/rewards/settings/add`)} />
			<ActiveRewardsTable
				existingRewards={allRewards?.rewards || []}
				setShowRewardDeleteModal={setShowRewardDeleteModal}
				totalPages={totalPages}
				page={page}
				handlePageChange={handlePageChange}
			/>
			<CustomModal
				open={showRewardDeleteModal}
				// setOpen={setShowRewardDeleteModal}
				// style={productDetailsModalStyles}
				children={
					<DeleteRewardModalContent
						onCancel={() => setShowRewardDeleteModal(null)}
						onConfirm={() => handleDeleteReward(showRewardDeleteModal)}
					/>
				}
				header={false}
			/>
		</Box>
	);
}

const addRewardsInitialValues = {
	rewardType: '',
	rewardTypeId: '',
	rewardDetails: null,
	conversionPercentage: '',
};

const AddRewardSchema = Yup.object().shape({
	rewardType: Yup.string().required('Required'),
	// required only if rewardsTYpe is product
	rewardTypeId: Yup.string().test('rewardTypeId', 'Required', function (value) {
		if (REWARD_TYPES.ALL_PRODUCTS !== this.parent.rewardType) {
			return value ? true : false;
		}
		return true;
	}),
	conversionPercentage: Yup.number()
		.required('Required')
		.min(1, 'Only Numbers between 1-100 are allowed')
		.max(100, 'Only Numbers between 1-100 are allowed')
		.typeError('Only Numbers Allowed'),
});

function AddReward({ apiService, id, setAlert, navigate }: any) {
	const [availableRewardsTypes, setAvailableRewardsTypes] = useState([]);
	const [isCreating, setIsCreating] = useState(false);
	const isMobile = IsMobileWidth();

	const { values, handleSubmit, handleChange, errors, setFieldValue } = useFormik({
		initialValues: addRewardsInitialValues,
		validationSchema: AddRewardSchema,
		onSubmit: (values) => {
			console.log(values);
			const payload = {
				rewardType: values.rewardType,
				rewardTypeId: values.rewardTypeId,
				conversionPercentage: parseFloat(values.conversionPercentage),
			};
			setIsCreating(true);
			apiService.createReward(
				payload,
				(response: any) => {
					console.log(response);
					setAlert({ message: 'Reward Added Successfully', severity: 'success' });
					setIsCreating(false);
					navigate(-1);
				},
				(error: any) => {
					console.log(error);
					setAlert({ message: error.response.data.message, severity: 'error' });
					setIsCreating(false);
				},
				id
			);
		},
	});
	const fetchRewardTypesFor = (type: any) => {
		const params = {
			rewardType: type,
		};
		apiService.getRewardTypes(
			(response: any) => {
				console.log(response);
				const rewardsAvailable = response?.data?.data?.rewardTypes || [];

				setAvailableRewardsTypes(
					rewardsAvailable.map((reward: any) => ({ ...reward, id: reward.id, value: reward.name }))
				);
			},
			(error: any) => {
				console.log(error);
			},
			id,
			params
		);
	};

	useEffect(() => {
		if (values?.rewardType) {
			fetchRewardTypesFor(values.rewardType);
		}
	}, [values]);

	return (
		<Stack gap={2} direction={'column'} p={2} width={400}>
			<LabeledSelectField
				label="Reward Type"
				placeholder="Eg ALL_IN_SUB_CATEGORY"
				options={REWARD_TYPES_OPTIONS}
				name="rewardType"
				value={values.rewardType}
				handleChange={handleChange}
				styles={{ width: '100%', height: '36px' }}
				errorText={errors.rewardType}
			/>
			{availableRewardsTypes && (
				<Stack>
					<AutoComplete
						//@ts-ignore
						selectedOption={values.rewardDetails}
						options={availableRewardsTypes || []}
						label="Reward Details"
						onChange={(newOption: any) => {
							console.log(newOption);
							handleChange({ target: { name: 'rewardDetails', value: newOption } });
							setFieldValue('rewardTypeId', newOption.id);
						}}
						placeholder={values.rewardType === REWARD_TYPES.ALL_PRODUCTS ? 'NONE' : 'Eg Belt'}
						renderOption={(props: any, item: any) => {
							return (
								<Box
									{...props}
									key={item?.id}
									display={'flex'}
									flexDirection={'column'}
									sx={{
										alignItems: 'flex-start !important',
									}}
								>
									<ListItemText>{item?.value}</ListItemText>
									<Typography sx={{ fontSize: '10px', color: 'gray' }}>
										{item?.category_name}
									</Typography>
								</Box>
							);
						}}
					/>
					{errors.rewardTypeId && errors.rewardTypeId !== '' && (
						<div style={{ color: 'red', fontSize: '12px' }}>{errors.rewardTypeId}</div>
					)}
				</Stack>
			)}

			{/* <LabeledSelectField
				label="Reward Details"
				placeholder={values.rewardType === REWARD_TYPES.ALL_PRODUCTS ? 'NONE' : 'Eg Belt'}
				options={availableRewardsTypes}
				name="rewardTypeId"
				value={values.rewardTypeId}
				handleChange={handleChange}
				styles={{ width: '100%', height: '36px' }}
				errorText={errors.rewardTypeId}
			/> */}

			<LabeledTextField
				label="Conversion %"
				value={''}
				name="conversionPercentage"
				value={values.conversionPercentage}
				onChange={handleChange}
				styles={{ width: '100%', height: '36px' }}
				placeholder="Eg 4"
				errorText={errors.conversionPercentage}
			/>
			<CustomButton
				title={isCreating ? <ButtonSpinner /> : 'Add New Reward'}
				handleClick={() => handleSubmit()}
				disabled={isCreating}
				className="addBtn"
			/>
			{isMobile && (
				<CustomButton title={'Back'} handleClick={() => navigate(-1)} className="back" />
			)}
		</Stack>
	);
}

function isRewardsSettingsHomePage(location: any, id: any) {
	return location === `/suppliers/${id}/rewards/settings`;
}

function RenderContent({ location, ...props }: any) {
	if (isRewardsSettingsHomePage(location, props.id)) {
		return <RewardsSettingsHome {...props} />;
	} else if (location === `/suppliers/${props.id}/rewards/settings/add`) {
		return <AddReward {...props} />;
	}
	return null;
}

function RewardsSettings() {
	const location = useLocation();
	const navigate = useNavigate();
	const { id } = useParams();
	const { apiService }: any = React.useContext(ApplicationContext);
	const [alert, setAlert] = useState({
		message: '',
		severity: 'success',
	});

	return (
		<StyledContainer>
			<PageHeader
				title="Rewards Settings"
				activeIcon={!isRewardsSettingsHomePage(location.pathname, id)}
				handleArrowBack={() => navigate(-1)}
			/>
			<RenderContent location={location.pathname} {...{ apiService, setAlert, id, navigate }} />
			{/* <AddReward /> */}
			{alert?.message !== '' ? <CustomAlert alert={alert} setAlert={setAlert} /> : null}
		</StyledContainer>
	);
}

export default RewardsSettings;
